/**
 * The app frame (prototype renderAll): aurora sky, title bar, the left
 * column with the glass dock, and the main pane. Hub renders into the
 * column and the main pane (Hub only since 2026-10-28:
 * docs/decisions/2026-10-28-hub-only.md).
 */
import { useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';
import { useT } from '../i18n';
import { useData } from '../store/data';
import { useUi, openSettings } from '../store/ui';
import { HubCol, HubMain } from '../hub/Hub';
import { SettingsDialog } from '../settings/Settings';
import { AdminDialog } from '../admin/Admin';
import { Icon } from '../ui/kit';
import { overlayOpen } from '../ui/overlay';
import { MeRow, VoiceBar, VoiceRow } from './Dock';
import { JumpDialog, openJump } from './Jump';
import { useVoice } from '../voice/store';
import markUrl from '../assets/mark.svg';
import { ModHost } from '../mod/ModDialogs';
import { NotifyDialogHost } from '../notify/NotifyDialog';
import { ProfileSheetHost } from '../hub/ProfileCard';
import { Lightbox } from '../hub/Message';
import { ReactionListHost } from '../hub/reactions';
import { PokeStack } from '../pokes/PokeStack';
import { StreamMini } from '../hub/StreamMini';
import { IncomingCalls } from '../hub/DmCall';
import { RecoveryAlert } from './Notices';
import { UpdateChip, UpdateReadyToast } from './Update';
import { EmailBanner, EmailDialogs } from '../account/Email';
import { ConfigBanners } from './ConfigNotices';
import { InviteLinkHost } from './InviteLink';
import { useAppName } from '../store/consoleDefaults';
import { StaffNotices } from './StaffNotices';
import { EventsHost } from '../events/Host';
import { desktopWindow } from '../lib/desktopGlue';
import { NotifyAskBar } from '../notify/NotifyAskBar';
import { setTitleName } from '../notify/title';
import { backDepth } from '@neosocial/ui/back';
import { watchLayout } from '@neosocial/ui/layout';
import { installEdgeSwipe } from '@neosocial/ui/touch';
import { closeNav, paneBack, readLayout, useLayout, usePanes, wirePanes } from './panes';

/** Brief gaps stay calm: "Reconnecting" only after 2.5 s without the gateway. */
function useCalmConnection(): 'ok' | 'reconnecting' {
  const c = useData((d) => d.connection);
  const [shown, setShown] = useState<'ok' | 'reconnecting'>('ok');
  useEffect(() => {
    if (c === 'ready') {
      const t = setTimeout(() => setShown('ok'), 0);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => setShown('reconnecting'), 2500);
    return () => clearTimeout(t);
  }, [c]);
  return shown;
}

/** "Reconnecting", after 2.5 s without the gateway (the title bar's end slot). */
function ConnChip() {
  const { t } = useT();
  const conn = useCalmConnection();
  if (conn !== 'reconnecting') return null;
  return (
    <span className="nx-chip ns-conn" data-tone="warn" role="status">
      <i className="nx-spin" />
      {t('ns.conn.reconnecting')}
    </span>
  );
}

/** A 10px caption glyph drawn with one device-pixel stroke (§6.2). */
function CapGlyph({ kind }: { kind: 'min' | 'max' | 'restore' | 'close' }) {
  return (
    <svg viewBox="0 0 10 10" aria-hidden="true">
      {kind === 'min' ? (
        <path d="M0 5.5h10" />
      ) : kind === 'max' ? (
        <rect x="0.5" y="0.5" width="9" height="9" />
      ) : kind === 'restore' ? (
        <path d="M2.5 2.5V.5h7v7h-2M.5 2.5h7v7h-7z" />
      ) : (
        <path d="M.5.5l9 9M9.5.5l-9 9" />
      )}
    </svg>
  );
}

/**
 * The frameless desktop window's caption buttons (L3), at the physical right
 * as Windows places them. Nothing on the web. Close hides to the tray, as
 * the native button did.
 */
function CaptionButtons() {
  const { t } = useT();
  const [win] = useState(desktopWindow);
  const [maxed, setMaxed] = useState(false);
  useEffect(() => {
    if (!win) return;
    let off: (() => void) | undefined;
    let live = true;
    const sync = () =>
      void win.isMaximized().then(
        (m) => live && setMaxed(m),
        () => undefined,
      );
    sync();
    win.onResized(sync).then(
      (f) => (live ? (off = f) : f()),
      () => undefined,
    );
    return () => {
      live = false;
      off?.();
    };
  }, [win]);
  if (!win) return null;
  const maxLabel = t(maxed ? 'ns.tb.restore' : 'ns.tb.maximize');
  return (
    <div className="nx-caps">
      <button
        type="button"
        className="nx-cap min"
        aria-label={t('ns.tb.minimize')}
        onClick={() => void win.minimize().catch(() => undefined)}
      >
        <CapGlyph kind="min" />
      </button>
      <button
        type="button"
        className="nx-cap max"
        aria-label={maxLabel}
        onClick={() => void win.toggleMaximize().catch(() => undefined)}
      >
        <CapGlyph kind={maxed ? 'restore' : 'max'} />
      </button>
      <button
        type="button"
        className="nx-cap close"
        aria-label={t('ns.tb.close')}
        onClick={() => void win.close().catch(() => undefined)}
      >
        <CapGlyph kind="close" />
      </button>
    </div>
  );
}

/** The window can be dragged by the title bar's empty parts (desktop). */
const DRAG = { 'data-tauri-drag-region': '' };

function TitleBar() {
  const { t } = useT();
  // the console's texts.app_name, on the tab and beside the mark (QA CO-BUG-15),
  // in sentence case and untracked (L13, T10)
  const appName = useAppName();
  useEffect(() => {
    // "(3) Neosocial" while something is unread (notify/badge.ts)
    setTitleName(appName);
  }, [appName]);
  return (
    <header className="nx-tb" dir="ltr" {...DRAG}>
      <div className="nx-tb-brand" {...DRAG}>
        <img className="nx-tb-mark" src={markUrl} alt="" width={16} height={16} {...DRAG} />
        <span className="nx-tb-name" dir="auto" {...DRAG}>
          {appName}
        </span>
        {/* the wordmark's tag of 58ceeb8 (owner, 2026-10-29) */}
        <span className="nx-wm-tag" {...DRAG}>
          ALPHA
        </span>
      </div>
      <button
        type="button"
        className="nx-cmdk"
        aria-keyshortcuts="Control+K"
        aria-label={t('ns.tb.jump')}
        onClick={(e) => openJump(e.currentTarget)}
      >
        <Icon name="search" size="s" />
        <span className="nx-cmdk-t">{t('tb.search')}</span>
        <kbd className="nx-cmdk-k">Ctrl K</kbd>
      </button>
      <div className="nx-tb-end" {...DRAG}>
        <UpdateChip />
        <UpdateReadyToast />
        <ConnChip />
        <CaptionButtons />
      </div>
    </header>
  );
}

/**
 * The frameless window's title bar outside the app: sign-in, offline,
 * connecting, maintenance and the console's off screen (App.tsx's frame).
 * The drag area, "Restart to update" and the caption buttons, as Discord's
 * login has; the screen below keeps its own brand. Desktop only: the web
 * has the browser's frame (QA DESK-1: the window could not be moved,
 * minimised or closed there).
 */
export function FrameTitleBar() {
  const [desktop] = useState(() => !!desktopWindow());
  if (!desktop) return null;
  return (
    <header className="nx-tb ns-frame-tb" dir="ltr" {...DRAG}>
      <div className="nx-tb-end" {...DRAG}>
        <UpdateChip />
        <UpdateReadyToast />
        <CaptionButtons />
      </div>
    </header>
  );
}

/** Global shortcuts, bound by KeyboardEvent.code (layout independent). */
function useShortcuts() {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const ctrl = e.ctrlKey || e.metaKey;
      if (ctrl && !e.shiftKey && e.code === 'Comma') {
        e.preventDefault();
        openSettings('appearance');
      } else if (ctrl && !e.shiftKey && e.code === 'KeyK' && !overlayOpen()) {
        e.preventDefault();
        openJump(document.querySelector('.nx-cmdk'));
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);
}

/**
 * The start of the main pane's header on a phone (Back, to the list) or a
 * tablet in portrait (the rail and sidebar drawer). Desktop: none (§12).
 */
function PaneButton({ kind }: { kind: 'back' | 'nav' }) {
  const { t } = useT();
  const label = t(kind === 'back' ? 'ns.pane.back' : 'ns.pane.nav');
  return (
    <button type="button" className="nx-pane-btn" aria-label={label} onClick={() => paneBack()}>
      <Icon name={kind === 'back' ? 'chev-left' : 'menu'} />
    </button>
  );
}

/**
 * Phones and tablets (§12): the layout on #nx-app (data-layout, data-pointer,
 * data-nav, data-pane), the pane stack and the left-edge swipe.
 */
function useMobileFrame(app: RefObject<HTMLDivElement | null>) {
  useLayoutEffect(() => watchLayout(app.current), [app]);
  useEffect(() => {
    wirePanes();
    return installEdgeSwipe(
      paneBack,
      () => {
        const l = readLayout();
        if (l.layout === 'phone') return backDepth() > 0;
        return l.layout === 'tablet' && l.nav === 'drawer';
      },
      '.nx-edge',
    );
  }, []);
}

export function Shell() {
  const { t } = useT();
  const settings = useUi((u) => u.settings);
  const admin = useUi((u) => u.admin);
  const voiceIdle = useVoice((v) => v.session.status === 'idle');
  const appRef = useRef<HTMLDivElement>(null);
  const dockRef = useRef<HTMLElement>(null);
  useShortcuts();
  useMobileFrame(appRef);
  const lay = useLayout();
  const pane = usePanes((p) => p.pane);
  const navOpen = usePanes((p) => p.navOpen);
  const [webShell] = useState(() => !desktopWindow());
  const phone = lay.layout === 'phone';
  const drawer = lay.layout === 'tablet' && lay.nav === 'drawer';
  // the compact voice bar where the dock is out of sight, unless the room itself shows
  const inRoom = useUi((u) => (u.hub?.kind === 'voice' && !u.hub.chat ? u.hub.channelId : null));
  const voiceCh = useVoice((v) => v.session.channelId ?? v.session.targetId);
  const showBar = !voiceIdle && inRoom !== voiceCh && ((phone && pane === 'main') || drawer);
  const colAway = (phone && pane === 'main') || (drawer && !navOpen);
  const mainAway = (phone && pane === 'home') || (drawer && navOpen);
  // the dock card sits under the rail and the sidebar (§6.7): --dock-h is its
  // measured height plus the 8px gap above it (100 + 8 in voice, 56 + 8 idle)
  useLayoutEffect(() => {
    const d = dockRef.current;
    const app = appRef.current;
    if (!d || !app || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() =>
      app.style.setProperty('--dock-h', Math.ceil(d.offsetHeight + 8) + 'px'),
    );
    ro.observe(d);
    return () => ro.disconnect();
  }, []);
  return (
    <div
      id="nx-app"
      ref={appRef}
      data-shell={webShell ? 'web' : 'desktop'}
      data-pane={phone ? pane : undefined}
      data-nav-open={drawer && navOpen ? '' : undefined}
    >
      <div className="nx-sky nx-tb-sky" aria-hidden="true">
        <i className="aur aur-hub" />
        <i className="grain" />
      </div>
      <TitleBar />
      {/* notices take their own row under the title bar: they push the app
          down and never cover it (account recovery, email verification,
          console banners; §7.20) */}
      <div className="nx-notices">
        {/* phones and tablets on the web have no title bar: its chip comes here */}
        {webShell && lay.layout !== 'desktop' ? <ConnChip /> : null}
        <RecoveryAlert />
        <NotifyAskBar />
        <EmailBanner />
        <ConfigBanners />
      </div>
      <InviteLinkHost />
      <div className="nx-body">
        <aside className="nx-col" aria-label={t('a11y.col')} inert={colAway || undefined}>
          <div className="nx-col-body" id="nx-col" data-mode="hub">
            <HubCol />
          </div>
          <section
            className="nx-dock nx-raised"
            id="nx-dock"
            ref={dockRef}
            aria-label={t('dock.you')}
          >
            <div className="nx-dock-voice" aria-live="polite" hidden={voiceIdle}>
              <VoiceRow />
            </div>
            <MeRow />
          </section>
        </aside>
        <main
          className="nx-main"
          id="nx-main"
          data-mode="hub"
          aria-label={t('a11y.main')}
          inert={mainAway || undefined}
        >
          {phone || drawer ? <PaneButton kind={phone ? 'back' : 'nav'} /> : null}
          <HubMain />
          {showBar ? <VoiceBar /> : null}
        </main>
        {drawer ? (
          <div className="nx-nav-scrim" aria-hidden="true" onClick={() => closeNav()} />
        ) : null}
        {/* the left-edge swipe's strip: horizontal moves come here, not to a scroller */}
        {phone || drawer ? <div className="nx-edge" aria-hidden="true" /> : null}
        {/* a stream you watch, while its voice room is away (Discord's pop-out):
            over both panes, under the notices */}
        <StreamMini />
      </div>
      {settings ? <SettingsDialog section={settings} /> : null}
      {admin ? <AdminDialog state={admin} /> : null}
      <JumpDialog />
      <ModHost />
      <NotifyDialogHost />
      {/* the profile card as a sheet on a phone (§12.7 B4) */}
      <ProfileSheetHost />
      {/* one image viewer for the app (chat images, profile pictures) and who reacted */}
      <Lightbox />
      <ReactionListHost />
      <PokeStack />
      {/* scheduled events: the list, an event, the editor, the start notice */}
      <EventsHost />
      {/* a DM call ringing you (2026-11-01) */}
      <IncomingCalls />
      <EmailDialogs />
      <StaffNotices />
    </div>
  );
}
